import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Container, Row, Col } from 'react-bootstrap';
import { FaArrowRight, FaChevronRight, FaMicrophone, FaBorderAll } from 'react-icons/fa';
import { SiGmail, SiSlack, SiGoogledocs, SiNotion, SiWhatsapp, SiMicrosoftteams, SiVisualstudiocode } from 'react-icons/si';
import './ProductsPage.css';

/* ─── Animated Waveform Bars ─── */
function Waveform({ active }) {
  const bars = [3, 7, 5, 9, 4, 8, 6, 10, 5, 7, 3, 9, 6, 8, 4];
  return (
    <div className="pp-waveform">
      {bars.map((h, i) => (
        <motion.div
          key={i}
          className="pp-wave-bar"
          animate={active ? { height: [h * 2, h * 4, h * 2] } : { height: 4 }}
          transition={
            active
              ? { duration: 0.7, repeat: Infinity, delay: i * 0.06, ease: 'easeInOut' }
              : { duration: 0.3 }
          }
          style={{ height: 4 }}
        />
      ))}
    </div>
  );
}

/* ─── Voice Capture Mockup ─── */
function VoiceMockup() {
  const [step, setStep] = useState('idle');
  const [displayText, setDisplayText] = useState('');

  const rawText = '"Schedule a follow-up with the client next Tuesday and send them the proposal document."';
  const enhancedText = 'Please schedule a follow-up meeting with the client for next Tuesday and share the updated proposal document with them.';

  useEffect(() => {
    const timeouts = [];
    const cycle = () => {
      setStep('idle');
      setDisplayText('');
      const t1 = setTimeout(() => setStep('listening'), 1200);
      const t2 = setTimeout(() => {
        setStep('transcribed');
        let i = 0;
        const interval = setInterval(() => {
          i++;
          setDisplayText(rawText.slice(0, i));
          if (i >= rawText.length) clearInterval(interval);
        }, 28);
        timeouts.push(interval);
      }, 2800);
      const t3 = setTimeout(() => { setStep('enhanced'); setDisplayText(enhancedText); }, 6000);
      const t4 = setTimeout(cycle, 11000);
      timeouts.push(t1, t2, t3, t4);
    };
    cycle();
    return () => timeouts.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dotClass =
    step === 'listening' ? 'pp-dot-active' : step === 'idle' ? 'pp-dot-idle' : 'pp-dot-done';

  return (
    <div className="pp-mockup-window">
      <div className="pp-mockup-chrome">
        <div className="pp-window-dots">
          <span className="pp-wd-red" /><span className="pp-wd-yellow" /><span className="pp-wd-green" />
        </div>
        <div className="pp-chrome-title">
          <div className="pp-chrome-icon"><FaMicrophone /></div>
          <span className="pp-chrome-label">ThinkingHub Assist · Voice Capture</span>
        </div>
        <span className="pp-hotkey-pill">⌘ + Shift + V</span>
      </div>

      <div className="pp-mockup-body">
        <div className="pp-status-row">
          <div className="pp-status-left">
            <motion.div
              animate={step === 'listening' ? { scale: [1, 1.2, 1], opacity: [1, 0.6, 1] } : { scale: 1 }}
              transition={{ duration: 1, repeat: step === 'listening' ? Infinity : 0 }}
              className={`pp-status-dot ${dotClass}`}
            />
            <span className="pp-status-text">
              {step === 'idle' && 'Ready — press trigger to speak'}
              {step === 'listening' && 'Listening…'}
              {step === 'transcribed' && 'Transcribed'}
              {step === 'enhanced' && 'Writing Improved ✓'}
            </span>
          </div>
          <span className="pp-platform-pill">Windows · macOS</span>
        </div>

        <div className="pp-waveform-wrap">
          <Waveform active={step === 'listening'} />
        </div>

        <div className="pp-output-area">
          <AnimatePresence mode="wait">
            {step === 'idle' && (
              <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pp-idle-state">
                <FaMicrophone className="pp-idle-mic" /> Speak naturally to start dictating…
              </motion.div>
            )}
            {(step === 'transcribed' || step === 'listening') && displayText && (
              <motion.div key="raw" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                <p className="pp-output-badge pp-badge-purple">Raw Transcript</p>
                <p className="pp-output-mono">{displayText}</p>
                <p className="pp-accuracy-hint">98.2% accuracy · 0.8s latency</p>
              </motion.div>
            )}
            {step === 'enhanced' && (
              <motion.div key="enhanced" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
                <p className="pp-output-badge pp-badge-pink">Writing Improved ✦</p>
                <p className="pp-output-text">{displayText}</p>
                <div className="pp-inline-btns">
                  <button className="pp-ibtn pp-ibtn-copy">Copy</button>
                  <button className="pp-ibtn pp-ibtn-paste">Paste to App</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="pp-mode-pills">
          {[
            { label: 'Transcribe only', active: step === 'transcribed' || step === 'idle' },
            { label: 'Fix my errors', active: false },
            { label: 'Improve writing', active: step === 'enhanced' },
          ].map((m) => (
            <span key={m.label} className={`pp-mode-pill ${m.active ? 'pp-mode-pill-on' : ''}`}>{m.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Writing Assistance Mockup ─── */
const writingModes = [
  {
    id: 'transcribe', icon: '🎙️',
    label: "I'm a confident speaker",
    sublabel: 'Transcribe only — no corrections or changes',
    rawText: 'we need to schedule the team sync for thursday afternoon and also send the agenda beforehand so everyone can prepare',
    outputText: 'we need to schedule the team sync for thursday afternoon and also send the agenda beforehand so everyone can prepare',
    outputLabel: 'Exact transcription — your words, unchanged',
    accentVar: 'var(--pp-primary)',
  },
  {
    id: 'fix', icon: '✏️',
    label: 'Just fix my errors',
    sublabel: 'Fix grammar and filler words for longer text (>10 words)',
    rawText: 'um we need to schedule the team sync for thursday afternoon and also like send the agenda beforehand so everyone can you know prepare',
    outputText: 'We need to schedule the team sync for Thursday afternoon and also send the agenda beforehand so everyone can prepare.',
    outputLabel: 'Grammar fixed · Filler words removed',
    accentVar: '#60a5fa',
  },
  {
    id: 'improve', icon: '✨',
    label: 'Help improve my writing',
    sublabel: 'Actively improve clarity, tone, and flow for all text',
    rawText: 'we need to schedule the team sync for thursday afternoon and also send the agenda beforehand so everyone can prepare',
    outputText: 'Please schedule the team sync for Thursday afternoon. To ensure everyone comes prepared, kindly share the agenda in advance.',
    outputLabel: 'Clarity improved · Tone polished · Flow enhanced',
    accentVar: 'var(--pp-accent)',
  },
];

function WritingAssistanceMockup() {
  const [active, setActive] = useState(0);
  return (
    <div className="pp-mockup-window">
      <div className="pp-mockup-chrome">
        <div className="pp-window-dots">
          <span className="pp-wd-red" /><span className="pp-wd-yellow" /><span className="pp-wd-green" />
        </div>
        <div className="pp-chrome-title">
          <div className="pp-chrome-icon"><FaMicrophone /></div>
          <span className="pp-chrome-label">ThinkingHub Assist · Writing Assistance</span>
        </div>
      </div>
      <div className="pp-mockup-body">
        <p className="pp-wa-hint">Choose how much help you need with your dictation</p>
        <div className="pp-wa-list">
          {writingModes.map((m, i) => (
            <button key={m.id} onClick={() => setActive(i)}
              className={`pp-wa-row ${active === i ? 'pp-wa-row-active' : ''}`}
            >
              <div className={`pp-radio-ring ${active === i ? 'pp-radio-ring-active' : ''}`}>
                {active === i && <div className="pp-radio-fill" />}
              </div>
              <div>
                <div className="pp-wa-label-row">
                  <span>{m.icon}</span>
                  <span className={`pp-wa-label ${active === i ? 'pp-wa-label-on' : ''}`}>{m.label}</span>
                </div>
                <p className="pp-wa-sublabel">{m.sublabel}</p>
              </div>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3 }} className="pp-wa-output">
            <div className="pp-wa-spoken">
              <p className="pp-wa-block-label">You spoke</p>
              <p className="pp-wa-spoken-text">"{writingModes[active].rawText}"</p>
            </div>
            <div className="pp-wa-result">
              <p className="pp-wa-block-label" style={{ color: writingModes[active].accentVar }}>ThinkingHub Assist output</p>
              <p className="pp-wa-result-text">{writingModes[active].outputText}</p>
              <div className="pp-wa-footer">
                <span className="pp-wa-note" style={{ color: writingModes[active].accentVar }}>{writingModes[active].outputLabel}</span>
                <div className="pp-inline-btns">
                  <button className="pp-ibtn pp-ibtn-copy">Copy</button>
                  <button className="pp-ibtn pp-ibtn-paste">Paste</button>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ─── Data ─── */
const features = [
  { icon: '⚡', title: 'Custom Hotkey Trigger', desc: 'Set any key combo — ⌘ + Shift + V, Ctrl + Space — to start capturing voice instantly without touching the mouse.', g1: '#9b5df5', g2: '#7303c0' },
  { icon: '🖥️', title: 'Windows & macOS Support', desc: 'Fully native support for Windows 10/11 and macOS. Works seamlessly on any desktop — no browser extension required.', g1: '#3b82f6', g2: '#06b6d4' },
  { icon: '✨', title: 'Writing Assistance', desc: "Choose transcribe only, fix grammar and filler words, or actively improve clarity and tone. You're always in control.", g1: '#9b5df5', g2: '#ec38bc' },
  { icon: '📋', title: 'Paste Into Any App', desc: 'Works everywhere — Gmail, Slack, Notion, Google Docs, VS Code. One click pastes polished text directly into your active window.', g1: '#10b981', g2: '#14b8a6' },
  { icon: '🎨', title: 'Style Matching', desc: 'ThinkingHub Assist learns how you write — your tone, vocabulary, formatting habits — and adapts every output to sound like you.', g1: '#f59e0b', g2: '#f97316' },
  { icon: '🔇', title: 'Noise Resilience', desc: 'Optimized for real-world environments — open offices, coffee shops, or quiet rooms. Speech captured cleanly regardless of noise.', g1: '#ef4444', g2: '#ec4899' },
  { icon: '🔒', title: 'Privacy First', desc: 'On-device processing options keep your voice data local. Zero audio retention — your voice is processed and discarded, never stored.', g1: '#6366f1', g2: '#3b82f6' },
];

const steps = [
  { icon: '⌨️', step: '01', title: 'Press your trigger', desc: 'Hit your custom hotkey from anywhere — any app, any window — to activate ThinkingHub Assist instantly.' },
  { icon: '🎙️', step: '02', title: 'Speak naturally', desc: 'Dictate in English or any supported language. Casual speech, bullet points, commands — it handles everything.' },
  { icon: '✨', step: '03', title: 'AI refines your words', desc: 'Grammar is fixed, style is matched, and your message is polished to exactly the tone you need.' },
  { icon: '📋', step: '04', title: 'Paste & done', desc: 'One click sends the cleaned, enhanced text directly into your active application — email, doc, or chat.' },
];

const relatedServices = [
  { name: 'NetSuite Managed Services', tagline: 'Ongoing support & optimization', desc: 'End-to-end NetSuite management so your team can focus on growth — not system administration.', href: '/netsuite/services', tag: 'Managed Services', g1: '#9b5df5', g2: '#7303c0' },
  { name: 'NetSuite ERP', tagline: 'Unified cloud ERP platform', desc: 'Consolidate your entire business — from financials to supply chain — in one intelligent NetSuite cloud platform.', href: '/netsuite/services/erp', tag: 'Enterprise ERP', g1: '#3b82f6', g2: '#06b6d4' },
  { name: 'NetSuite Implementation', tagline: 'Expert setup & go-live', desc: 'A successful NetSuite ERP implementation requires expertise and precision — our certified team delivers both.', href: '/netsuite/services/implementation', tag: 'Implementation', g1: '#f59e0b', g2: '#f97316' },
];

const apps = [
  { name: 'Gmail',       icon: <SiGmail />,            color: '#EA4335', bg: '#fef2f2' },
  { name: 'Slack',       icon: <SiSlack />,            color: '#4A154B', bg: '#f5f0f5' },
  { name: 'Google Docs', icon: <SiGoogledocs />,       color: '#4285F4', bg: '#eff6ff' },
  { name: 'Notion',      icon: <SiNotion />,           color: '#000000', bg: '#f3f3f3' },
  { name: 'WhatsApp',    icon: <SiWhatsapp />,         color: '#25D366', bg: '#f0fdf4' },
  { name: 'MS Teams',    icon: <SiMicrosoftteams />,   color: '#6264A7', bg: '#f4f4fb' },
  { name: 'VS Code',     icon: <SiVisualstudiocode />, color: '#007ACC', bg: '#eff8ff' },
  { name: 'Any App',     icon: <FaBorderAll />,        color: '#0066CC', bg: '#eef5ff' },
];

/* ─── Main Page ─── */
const ProductsPage = () => (
  <>
    <Helmet>
      <title>Products | SPIRENEST TECHNOLOGIES</title>
      <link rel="canonical" href="https://spirenest.co/products" />
      <meta name="description" content="ThinkingHub Assist — AI-powered voice dictation and voice assistant for Windows and macOS. A product by SPIRENEST TECHNOLOGIES, a DBA of PS NET IT SOLUTIONS LLC." />
      <meta property="og:title" content="Products | SPIRENEST TECHNOLOGIES" />
      <meta property="og:description" content="ThinkingHub Assist: AI voice dictation & assistant for Windows and macOS by SPIRENEST TECHNOLOGIES." />
      <meta property="og:url" content="https://spirenest.co/products" />
    </Helmet>

    {/* ── Hero ── */}
    <section className="pp-hero">
      <div className="pp-dots-bg" />
      <div className="pp-glow-tl" /><div className="pp-glow-br" />
      <Container className="pp-hero-inner">
        <Row className="align-items-center g-5">
          <Col lg={6}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <div className="pp-hero-badge">
                <FaMicrophone className="pp-badge-mic" />
                <span>ThinkingHub Assist</span>
              </div>
              <h1 className="pp-hero-title">
                Stop Typing.{' '}
                <span className="pp-grad-text">Start Speaking.</span>
              </h1>
              <p className="pp-hero-sub">
                ThinkingHub Assist transforms your voice into polished, ready-to-send text in seconds.
                Dictate naturally, let AI refine your words, and paste directly into any
                application — no manual typing, ever again.
              </p>

              <div className="pp-stats">
                {[
                  { v: '3×', l: 'Faster Content Creation' },
                  { v: '98%', l: 'Transcription Accuracy' },
                  { v: '< 1s', l: 'Processing Latency' },
                ].map((s) => (
                  <div key={s.l} className="pp-stat">
                    <span className="pp-stat-v">{s.v}</span>
                    <span className="pp-stat-l">{s.l}</span>
                  </div>
                ))}
              </div>

              <div className="pp-chips">
                {['Content Creators', 'Remote Workers', 'Executives', 'Writers', 'Customer Support'].map((c) => (
                  <span key={c} className="pp-chip">{c}</span>
                ))}
              </div>

              <div className="pp-hero-btns">
                <Link to="/contact" className="pp-btn-primary">
                  Get Early Access <FaArrowRight className="pp-btn-arr" />
                </Link>
                <a href="#pp-action" className="pp-btn-ghost">See It in Action</a>
              </div>
            </motion.div>
          </Col>
          <Col lg={6}>
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
              <VoiceMockup />
            </motion.div>
          </Col>
        </Row>
      </Container>
    </section>

    {/* ── See It In Action ── */}
    <section id="pp-action" className="pp-section pp-dark2">
      <div className="pp-dots-bg pp-dots-sm" />
      <Container>
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="pp-sec-head">
          <div className="pp-sec-badge">See It In Action</div>
          <h2 className="pp-sec-title">Watch ThinkingHub Assist <span className="pp-grad-text">Work Its Magic</span></h2>
          <p className="pp-sec-sub">From a single hotkey press to polished text in your inbox — see every step.</p>
        </motion.div>

        <div className="pp-action-flow">
          {/* Step 1 */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <div className="pp-step-badge pp-step-badge-purple">
              <span className="pp-step-dot pp-dot-pulse" />
              Step 1 · Voice Capture
            </div>
            <VoiceMockup />
            <p className="pp-step-cap">Press your custom hotkey → speak freely → watch text appear in real time.</p>
          </motion.div>

          <div className="pp-flow-divider">
            <div className="pp-fdivider-line" />
            <div className="pp-fdivider-arrow">↓</div>
            <div className="pp-fdivider-line" />
          </div>

          {/* Step 2 */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <div className="pp-step-badge pp-step-badge-purple">
              <span className="pp-step-dot" />
              Step 2 · Writing Assistance — Choose Your Level
            </div>
            <WritingAssistanceMockup />
            <p className="pp-step-cap">Click any mode to see how ThinkingHub Assist processes the same spoken text differently.</p>
          </motion.div>

          <div className="pp-flow-divider">
            <div className="pp-fdivider-line" />
            <div className="pp-fdivider-arrow">↓</div>
            <div className="pp-fdivider-line" />
          </div>

          {/* Step 3 */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <div className="pp-step-badge pp-step-badge-pink">
              <span className="pp-step-dot pp-dot-pink" />
              Step 3 · Paste Into Any App
            </div>
            <div className="pp-apps-card">
              <p className="pp-apps-hint">One click — your polished text lands exactly where you need it</p>
              <div className="pp-apps-grid">
                {apps.map((app) => (
                  <motion.div key={app.name} whileHover={{ scale: 1.05, y: -3 }} transition={{ duration: 0.2 }} className="pp-app-tile">
                    <div className="pp-app-ico" style={{ background: app.bg, color: app.color }}>{app.icon}</div>
                    <span className="pp-app-name">{app.name}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            <p className="pp-step-cap">No copy-paste friction. ThinkingHub Assist injects polished text directly into your active window.</p>
          </motion.div>
        </div>
      </Container>
    </section>

    {/* ── Features ── */}
    <section className="pp-section pp-light">
      <Container>
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="pp-sec-head">
          <div className="pp-sec-badge">Features</div>
          <h2 className="pp-sec-title">Everything You Need to <span className="pp-grad-text">Write Faster</span></h2>
          <p className="pp-sec-sub">ThinkingHub Assist isn't just voice-to-text. It's an intelligent writing layer that makes every word count.</p>
        </motion.div>
        <Row className="g-3 mb-3">
          {features.slice(0, 4).map((f, i) => (
            <Col lg={3} md={6} key={f.title}>
              <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.06 * i }} className="pp-feat-card h-100">
                <div className="pp-feat-topbar" style={{ background: `linear-gradient(90deg, ${f.g1}, ${f.g2})` }} />
                <div className="pp-feat-body">
                  <div className="pp-feat-hrow">
                    <div className="pp-feat-ico" style={{ background: `linear-gradient(135deg, ${f.g1}, ${f.g2})` }}>{f.icon}</div>
                    <span className="pp-feat-num">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="pp-feat-title">{f.title}</h3>
                  <p className="pp-feat-desc">{f.desc}</p>
                </div>
              </motion.div>
            </Col>
          ))}
        </Row>
        <Row className="g-3 justify-content-center">
          {features.slice(4).map((f, i) => (
            <Col lg={3} md={6} key={f.title}>
              <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.06 * (i + 4) }} className="pp-feat-card h-100">
                <div className="pp-feat-topbar" style={{ background: `linear-gradient(90deg, ${f.g1}, ${f.g2})` }} />
                <div className="pp-feat-body">
                  <div className="pp-feat-hrow">
                    <div className="pp-feat-ico" style={{ background: `linear-gradient(135deg, ${f.g1}, ${f.g2})` }}>{f.icon}</div>
                    <span className="pp-feat-num">{String(i + 5).padStart(2, '0')}</span>
                  </div>
                  <h3 className="pp-feat-title">{f.title}</h3>
                  <p className="pp-feat-desc">{f.desc}</p>
                </div>
              </motion.div>
            </Col>
          ))}
        </Row>
      </Container>
    </section>

    {/* ── How It Works ── */}
    <section className="pp-section pp-dark2">
      <div className="pp-dots-bg pp-dots-sm" />
      <div className="pp-glow-top-center" />
      <Container style={{ position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="pp-sec-head">
          <div className="pp-sec-badge">How It Works</div>
          <h2 className="pp-sec-title">From Voice to Text in <span className="pp-grad-text">4 Simple Steps</span></h2>
          <p className="pp-sec-sub">No setup friction, no learning curve. ThinkingHub Assist fits into your existing workflow in minutes.</p>
        </motion.div>
        <Row className="g-4">
          {steps.map((s, i) => (
            <Col lg={3} md={6} key={s.step}>
              <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 * i }} className="pp-step-card">
                {i < steps.length - 1 && (
                  <div className="pp-step-conn">
                    <FaChevronRight className="pp-chevron" />
                  </div>
                )}
                <div className="pp-step-icon">{s.icon}</div>
                <span className="pp-step-num">{s.step}</span>
                <h3 className="pp-step-title">{s.title}</h3>
                <p className="pp-step-desc">{s.desc}</p>
              </motion.div>
            </Col>
          ))}
        </Row>
      </Container>
    </section>

    {/* ── CTA Banner ── */}
    <section className="pp-section pp-dark1 pp-cta">
      <div className="pp-cta-overlay" />
      <Container className="text-center" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <div className="pp-cta-live">
            <span className="pp-cta-pulse" />Early Access Open
          </div>
          <h2 className="pp-cta-title">
            Eliminate Manual Typing.{' '}
            <span className="pp-grad-text">Create 3× Faster.</span>
          </h2>
          <p className="pp-cta-sub">
            Join teams and individuals who have swapped keyboard typing for voice — and never looked back.
          </p>
          <div className="pp-cta-chips">
            {['Windows & macOS', 'Any Application', 'AI-Powered', 'Privacy First', 'Real-Time'].map((c) => (
              <span key={c} className="pp-cta-chip">{c}</span>
            ))}
          </div>
          <div className="pp-cta-btns">
            <Link to="/contact" className="pp-btn-primary pp-btn-lg">
              Request Early Access <FaArrowRight className="pp-btn-arr" />
            </Link>
            <Link to="/contact" className="pp-btn-ghost pp-btn-lg">Book a Demo</Link>
          </div>
        </motion.div>
      </Container>
    </section>

    {/* ── Related Services ── */}
    <section className="pp-section pp-light">
      <div className="pp-dots-bg pp-dots-sm" />
      <Container style={{ position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="pp-sec-head">
          <h2 className="pp-sec-title" style={{ fontSize: '1.8rem' }}>Explore More SpireNest Services</h2>
          <p className="pp-sec-sub">A complete suite of IT solutions built for modern businesses.</p>
        </motion.div>
        <Row className="g-4">
          {relatedServices.map((p, i) => (
            <Col md={4} key={p.name}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <Link to={p.href} className="pp-svc-card text-decoration-none d-block">
                  <div className="pp-svc-tag" style={{ background: `linear-gradient(135deg, ${p.g1}, ${p.g2})` }}>{p.tag}</div>
                  <h3 className="pp-svc-name">{p.name}</h3>
                  <p className="pp-svc-tagline">{p.tagline}</p>
                  <p className="pp-svc-desc">{p.desc}</p>
                  <div className="pp-svc-link">Learn more <FaArrowRight className="pp-svc-arr" /></div>
                </Link>
              </motion.div>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  </>
);

export default ProductsPage;
